// Packages
import React from 'react';

// Services
import { apiFetchData } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import { IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import {
    dispatchTrackingEvent,
    AI_SEARCH_RESULTS_GALLERY,
    AI_SEARCH_RESULTS_UNIT_CLICK,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';

// Parts
import Carousel from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/carousel';

// Icons
import {
    MdOutlinePercent,
    MdStar,
    MdArrowDropDown,
    MdArrowDropUp,
    MdWater,
    MdOutlineEditCalendar,
    MdArrowRight,
    MdOutlineStraighten,
    MdOutlineShower,
} from 'react-icons/md';
import { BiUser } from 'react-icons/bi';
import { BsDoorClosed } from 'react-icons/bs';

const renderImageSourceElements = (item) => item.imageSourceURLLargeSizeAlternative && (
    <source srcSet={item.imageSourceURLLargeSizeAlternative} media="(min-width: 4000px)" />
);

export default class SearchResultsAICard extends React.Component {
    constructor(props) {
        super(props);
        const { data } = this.props;
        this.numberOfDisplayedUnits = 2;
        this.lastDisplayedUnitIndex = this.numberOfDisplayedUnits - 1;
        this.breakpointMdPx = 992;
        // Logika prebačena iz lifecycle metode "componentDidMount" zbog toga da bi galerija bila dostupna i na SSR-u.
        let gallery = [];
        let unitIdToFirstGalleryImageIndexMapper = {};
        if (data && data.gallery && data.units) {
            gallery = [...data.gallery.items];
            unitIdToFirstGalleryImageIndexMapper = {};
            data.units.forEach((unit) => {
                unitIdToFirstGalleryImageIndexMapper[unit.unitId] = gallery.length;
                const unitImages = unit.gallery.items.map((image) => ({
                    ...image,
                    title: `${unit.type.toUpperCase()} ${unit.title}`,
                    unitId: unit.unitId,
                }));
                gallery.push(...unitImages);
            });
        }
        this.state = {
            gallery,
            selectedUnitId: null,
            isGalleryLoading: false,
            isGalleryLoaded: false,
            showAllUnits: false,
            unitIdToFirstGalleryImageIndexMapper,
            galleryIndexToJumpTo: null,
        };
    }

    onCurrentGalleryIndexChanged = async (index) => {
        const { gallery } = this.state;
        this.setState({ selectedUnitId: gallery[index].unitId ?? null });
    };

    jumpToGallerySection = (moveDirection) => {
        const { gallery, unitIdToFirstGalleryImageIndexMapper, selectedUnitId } = this.state;
        let indexOffset = 0;
        if (moveDirection) {
            indexOffset = moveDirection === 'next' ? 1 : -1;
        }
        const galleryLength = gallery.length;
        const nextIndex = ((selectedUnitId ? unitIdToFirstGalleryImageIndexMapper[selectedUnitId] + indexOffset : indexOffset)
                            + galleryLength) % galleryLength;
        this.setState({ galleryIndexToJumpTo: nextIndex, selectedUnitId: gallery[nextIndex].unitId ?? null }, () => {
            this.setState({ galleryIndexToJumpTo: null });
        });
    };

    handleShowMoreUnits = (event) => {
        event.stopPropagation();
        event.preventDefault();
        this.setState((prevState) => ({
            showAllUnits: !prevState.showAllUnits,
        }));
    };

    getAdditionalData = (moveDirection) => {
        const { gallery, isGalleryLoaded, isGalleryLoading } = this.state;
        if (isGalleryLoaded || isGalleryLoading) return;
        const { data } = this.props;
        this.setState({ isGalleryLoading: true });
        apiFetchData('accommodation/private/search/facility-gallery/', `/${data.facilityId}`)
            .then((res) => {
                if (res.status !== 200) {
                    reportError(res);
                    return;
                }
                const newFacilityImages = res.data.facilityGallery?.flatMap((item) => item.images) ?? [];
                const newGallery = [gallery[0], ...newFacilityImages];
                const unitIdToFirstGalleryImageIndexMapper = {};
                const { unitsGallery } = res.data;
                unitsGallery.forEach((unitGallery) => {
                    if (!data.units.some((unit) => unit.unitId === unitGallery.unitId)) return;
                    unitIdToFirstGalleryImageIndexMapper[unitGallery.unitId] = newGallery.length;
                    const newUnitImages = unitGallery.images?.map(
                        (image) => ({ ...image, title: unitGallery.title, unitId: unitGallery.unitId }),
                    ) ?? [];
                    newGallery.push(
                        gallery.find((image) => image.unitId === unitGallery.unitId),
                        ...newUnitImages,
                    );
                });
                this.setState({
                    gallery: newGallery,
                    unitIdToFirstGalleryImageIndexMapper,
                    isGalleryLoaded: true,
                    isGalleryLoading: false,
                }, () => {
                    this.jumpToGallerySection(moveDirection);
                    if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_RESULTS_GALLERY);
                });
            })
            .catch((err) => reportError(err))
            .finally(() => this.setState({ isGalleryLoading: false }));
    };

    handleOnMouseLeave = () => {
        const { selectedUnitId } = this.state;
        if (!selectedUnitId) return;
        this.setState({ selectedUnitId: null }, () => {
            this.jumpToGallerySection();
        });
    };

    handleUnitSelect = (unitId) => {
        if (window.innerWidth < this.breakpointMdPx) return;
        this.setState({ selectedUnitId: unitId }, () => {
            this.jumpToGallerySection();
        });
    };

    render() {
        const { data, firstCarouselImageLazy, isForMap, onOpenModal } = this.props;
        const { showAllUnits, gallery, isGalleryLoaded, isGalleryLoading, galleryIndexToJumpTo } = this.state;
        const imageWidth = isForMap ? 300 : 360;
        const imageHeight = isForMap ? 200 : 240;

        return data && (
            <div className="search-result-card" onMouseLeave={this.handleOnMouseLeave}>
                <div role="presentation" className="search-result-card__carousel">
                    {gallery.length > 0 && (
                        <Carousel
                            data={{ items: gallery, id: data.facilityId }}
                            isGalleryLoaded={isGalleryLoaded}
                            autoplay={false}
                            usedOnMap={isForMap}
                            imageWidth={imageWidth}
                            imageHeight={imageHeight}
                            imageSourceElements={renderImageSourceElements}
                            customObjectFit="cover"
                            firstImageLazy={firstCarouselImageLazy}
                            galleryFetching={isGalleryLoading}
                            onCurrentIndexChanged={this.onCurrentGalleryIndexChanged}
                            fetchGallery={this.getAdditionalData}
                            indexToJumpTo={galleryIndexToJumpTo}
                        />
                    )}
                    {data.hasActiveDiscount && (
                        <div className="search-result-card__discount">
                            <span className="search-result-card__discount-text">{data.discountPercentText || <MdOutlinePercent className="search-result-card__discount-svg" />}</span>
                        </div>
                    )}
                    {/* <button type="button" aria-label="Add to favorites" className={`search-result-card__carousel-heart ${data.facilityId === 13018 ? 'search-result-card__carousel-heart--filled' : ''}`}>
                        {data.facilityId === 13018 ? (
                            <BiSolidHeart size="20" className="search-result-card__carousel-heart-icon" />
                        ) : (
                            <BiHeart size="20" className="search-result-card__carousel-heart-icon" />
                        )}
                    </button> */}
                </div>
                <h3
                    className="search-result-card__title"
                    // eslint-disable-next-line
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                        e.preventDefault();
                        const firstUnit = data.units?.[0];
                        if (firstUnit && onOpenModal) {
                            onOpenModal(firstUnit.resultUnitURL);
                            if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_RESULTS_UNIT_CLICK);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            const firstUnit = data.units?.[0];
                            if (firstUnit && onOpenModal) {
                                onOpenModal(firstUnit.resultUnitURL);
                                if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_RESULTS_UNIT_CLICK);
                            }
                        }
                    }}
                >
                    {data.title}
                </h3>
                {data.rating && (
                    <span className="search-result-card__rating">
                        <MdStar size="14" />
                        {data.rating}
                    </span>
                )}
                {data.distaceToBeach && (
                    <p className="search-result-card__beach-distance">
                        <MdWater size="16" className="search-result-card__beach-distance-icon" />
                        <span>{data.distaceToBeach}</span>
                    </p>
                )}
                {data.freeCancel && (
                    <p className="search-result-card__free-cancel">
                        <MdOutlineEditCalendar size="16" className="search-result-card__free-cancel-icon" />
                        <span>{data.freeCancel}</span>
                    </p>
                )}
                <div className="search-result-card__units">
                    {data.units.map((unit, unitIndex) => (
                        <a
                            key={`unit-details-${unit.unitId}`}
                            href={unit.resultUnitURL}
                            className={`search-result-card__unit
                                ${(!showAllUnits && (unitIndex > this.lastDisplayedUnitIndex)) ? 'd-none' : ''}
                            `}
                            onMouseEnter={() => this.handleUnitSelect(unit.unitId)}
                            onFocus={() => this.handleUnitSelect(unit.unitId)}
                            data-id={unit.unitId}
                            onClick={(e) => {
                                e.preventDefault();
                                if (onOpenModal) {
                                    onOpenModal(unit.resultUnitURL);
                                    if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_RESULTS_UNIT_CLICK);
                                }
                            }}
                            rel="noreferrer"
                            data-js-target="_blank"
                            aria-label={unit.fullTitle}
                        >
                            <p className="d-none">{unit.fullTitle}</p>
                            <span className="search-result-card__unit-title">
                                <span className="search-result-card__unit-title-text" title={`${unit.type} ${unit.title}`}>
                                    <span className="text-uppercase">{unit.type}</span> {unit.title}
                                </span>
                                <span className="search-result-card__unit-title-horizontal-line d-lg-none" />
                            </span>
                            <span className="search-result-card__unit-details">
                                <span className="search-result-card__unit-vertical-line d-none d-lg-inline" />
                                <span className="search-result-card__unit-space">
                                    <MdOutlineStraighten size="18" className="search-result-card__unit-space-icon" />
                                    <span dangerouslySetInnerHTML={{ __html: unit.space }} />
                                </span>
                                <span className="search-result-card__unit-dot search-result-card__unit-dot--order-4" />
                                <span className="search-result-card__unit-rooms">
                                    <BsDoorClosed size="18" className="search-result-card__unit-rooms-icon" />
                                    {unit.rooms}
                                </span>
                                <span className="search-result-card__unit-dot search-result-card__unit-dot--order-6" />
                                <span className="search-result-card__unit-bathrooms">
                                    <MdOutlineShower size="18" className="search-result-card__unit-bathrooms-icon" />
                                    {unit.bathrooms}
                                </span>
                                <span className="search-result-card__unit-dot" />
                                <span className="search-result-card__unit-people">
                                    <BiUser size="18" className="search-result-card__unit-people-icon" />
                                    {unit.people}
                                </span>
                            </span>
                            <span className="search-result-card__unit-price-box">
                                {unit.priceTextWithoutDiscount && (
                                    <span className="search-result-card__unit-price-without-discount" dangerouslySetInnerHTML={{ __html: unit.priceTextWithoutDiscount }} />
                                )}
                                <span className="search-result-card__unit-price" dangerouslySetInnerHTML={{ __html: unit.priceText }} />
                            </span>
                            <span className="search-result-card__unit-arrow-right">
                                <MdArrowRight size="24" className="search-result-card__unit-arrow-right-icon" />
                            </span>
                        </a>
                    ))}
                    {data.units?.length > this.numberOfDisplayedUnits && (
                        <button type="button" onClick={this.handleShowMoreUnits} className="search-result-card__show-all-units">
                            {showAllUnits ? (
                                <>{data.showLessUnitsText} <MdArrowDropUp size="20" /></>
                            ) : (
                                <>{data.showAllUnitsText} <MdArrowDropDown size="20" /></>
                            )}
                        </button>
                    )}
                </div>
            </div>
        );
    }
}
